import {
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnInit,
} from '@angular/core';
import { CommonModule, registerLocaleData } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';

import localeSrLatn from '@angular/common/locales/sr-Latn';
import localeEnGB from '@angular/common/locales/en-GB';
import localeDa from '@angular/common/locales/da';
import localeNb from '@angular/common/locales/nb';
import localeIt from '@angular/common/locales/it';
import localeDe from '@angular/common/locales/de';
import localeFr from '@angular/common/locales/fr';
import localeHr from '@angular/common/locales/hr';
import localeEs from '@angular/common/locales/es';
import localeSv from '@angular/common/locales/sv';
import localeRu from '@angular/common/locales/ru';
import localeAr from '@angular/common/locales/ar';

import { TabsStateService } from './shared/services/tabs-state.service';
import { StorageService } from '@anihis/shared/src/services/storage.service';
import { StorageKeyConstants } from '../../../../libs/shared/src/constants/storage-key-constants';
import { AuthenticationService } from './../../../../libs/shared/src/services/authentication.service';

// ⬇️ opcionalno: ako koristiš onaj TokenService event-bus za unauthorized
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { from, of } from 'rxjs';
import { catchError, switchMap, tap, map } from 'rxjs/operators';
import { TokenService } from './shared/services/token.service';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'anihis-root',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  private transloco = inject(TranslocoService);
  private tabsState = inject(TabsStateService);
  private auth = inject(AuthenticationService);
  private router = inject(Router);
  private tokenEvents = inject(TokenService, { optional: true });
  private dialog = inject(MatDialog);
  private storageService = inject(StorageService);

  // ➕ Dodaj DestroyRef
  private destroyRef = inject(DestroyRef);

  get userRole(): string | null {
    return this.auth.getRoleFromToken?.() ?? null;
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    const keyCode = (event as KeyboardEvent & { which?: number }).which;
    const isF5 =
      event.key === 'F5' ||
      event.code === 'F5' ||
      event.keyCode === 116 ||
      keyCode === 116;
    const isRefreshShortcut =
      (event.metaKey || event.ctrlKey) &&
      event.key.toLowerCase() === 'r' &&
      !event.altKey;

    if (!isF5 && !isRefreshShortcut) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.tabsState.createOpenTabs('Cards');
  }

  constructor() {
    // Start async language initialization (no top-level await in constructor)
    this.initLanguage();
  }

  private initLanguage(): void {
    const defaultLang = 'en-GB';

    from(
      this.storageService.getItem<string | undefined>(
        StorageKeyConstants.LANGUAGE_KEY,
      ),
    )
      .pipe(
        catchError(() => of(undefined)),
        switchMap((savedLang) => {
          if (!savedLang && typeof localStorage !== 'undefined') {
            const legacy = localStorage.getItem('lang');
            if (legacy) {
              return from(
                this.storageService.setItem(
                  StorageKeyConstants.LANGUAGE_KEY,
                  legacy,
                ),
              ).pipe(
                tap(() => {
                  localStorage.removeItem('lang');
                }),
                map(() => legacy),
                catchError((e) => {
                  return of(undefined);
                }),
              );
            }
          }
          return of(savedLang);
        }),
        tap((savedLang) => {
          const lang = savedLang ?? defaultLang;

          this.transloco.setAvailableLangs([
            'ar-SA',
            'da-DK',
            'de-DE',
            'en-GB',
            'es-ES',
            'fr-FR',
            'hr-HR',
            'it-IT',
            'nb-NO',
            'ru-RU',
            'sr-Latn',
            'sv-SE',
          ]);
          this.transloco.setActiveLang(lang);

          if (lang === 'en-GB') {
            registerLocaleData(localeEnGB, 'en-GB');
          } else if (lang === 'da-DK') {
            registerLocaleData(localeDa, 'da-DK');
          } else if (lang === 'nb-NO') {
            registerLocaleData(localeNb, 'nb-NO');
          } else if (lang === 'it-IT') {
            registerLocaleData(localeIt, 'it-IT');
          } else if (lang === 'de-DE') {
            registerLocaleData(localeDe, 'de-DE');
          } else if (lang === 'fr-FR') {
            registerLocaleData(localeFr, 'fr-FR');
          } else if (lang === 'hr-HR') {
            registerLocaleData(localeHr, 'hr-HR');
          } else if (lang === 'es-ES') {
            registerLocaleData(localeEs, 'es-ES');
          } else if (lang === 'sv-SE') {
            registerLocaleData(localeSv, 'sv-SE');
          } else if (lang === 'ru-RU') {
            registerLocaleData(localeRu, 'ru-RU');
          } else if (lang === 'ar-SA') {
            registerLocaleData(localeAr, 'ar-SA');
          } else {
            registerLocaleData(localeSrLatn, 'sr-Latn');
          }
        }),
        switchMap((savedLang) => {
          if (!savedLang) {
            return from(
              this.storageService.setItem(
                StorageKeyConstants.LANGUAGE_KEY,
                defaultLang,
              ),
            ).pipe(catchError(() => of(undefined)));
          }
          return of(undefined);
        }),
      )
      .subscribe();
  }

  ngOnInit(): void {
    // startupRefresh() is called via APP_INITIALIZER before routing begins.
    // Here we only listen for the unauthorized event (refresh failed → logout).
    if (this.tokenEvents) {
      this.tokenEvents.isUnauthorized$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.dialog.closeAll();
          this.auth.logout?.();
          this.router.navigateByUrl('/login');
        });
    }
  }
}
